import React, {lazy} from 'react';
import {AuthLayout} from 'components';
import {PublicRoute} from 'components/HOCs';
import {PublicSingleAudioLayout} from 'components/Layouts/PublicSingleAudioLayout';
import {ROUTES} from 'constants/routes';
import {InsuranceStatus} from 'features/DirectBooking/constants';
import {ArticleLayout} from 'features/Provider/Marketing/Public/ArticleLayout';
import {AudioLayout} from 'features/Provider/Marketing/Public/AudioLayout';
import {useLocale, useQuery} from 'hooks';
import {Redirect} from 'react-router-dom';
import {componentLoader} from 'utils';

const WelcomePage = lazy(() =>
  componentLoader(() => import('features/User/Welcome')),
);
const LoginPage = lazy(() =>
  componentLoader(() => import('features/User/Login')),
);
const GetStartedPage = lazy(() =>
  componentLoader(() => import('features/User/GetStarted')),
);
const RegisterPage = lazy(() =>
  componentLoader(() => import('features/User/Register')),
);
const RequestPasswordTokenPage = lazy(() =>
  componentLoader(() => import('features/User/ResetPassword/RequestToken')),
);
const ValidateResetTokenPage = lazy(() =>
  componentLoader(() => import('features/User/ResetPassword/ValidateToken')),
);
const ChangePasswordPage = lazy(() =>
  componentLoader(() => import('features/User/ResetPassword/ChangePassword')),
);
const ReferralPage = lazy(() =>
  componentLoader(() => import('features/User/Referral')),
);
const PlansPage = lazy(() =>
  componentLoader(() => import('features/Payment/Plans')),
);

const DirectBookingPage = lazy(() =>
  componentLoader(() => import('features/DirectBooking')),
);
const DirectBookingSearchProvidersPage = lazy(() =>
  componentLoader(() => import('features/DirectBooking/SearchProviders')),
);
const DirectBookingProviderListPage = lazy(() =>
  componentLoader(() => import('features/DirectBooking/ProviderList')),
);
const DirectBookingProviderPage = lazy(() =>
  componentLoader(() => import('features/DirectBooking/Provider')),
);

const RegisterPracticePage = lazy(() =>
  componentLoader(
    () => import('features/User/Register/OutsideWellniteRegister'),
  ),
);

const ProviderRegister = lazy(() =>
  componentLoader(() => import('features/Provider/Register/Signup')),
);

const ProviderThankYou = lazy(() =>
  componentLoader(
    () => import('features/Provider/Register/Signup/ProviderThankYou'),
  ),
);

const SelfAwarenessPage = lazy(() =>
  componentLoader(() => import('features/Member/SelfAwareness')),
);

const MinorConsent = lazy(() =>
  componentLoader(
    () => import('features/Member/Onboarding/MinorConsent/SignMinorConsent'),
  ),
);

const IntakeLoginPage = lazy(() =>
  componentLoader(() => import('features/Intake/IntakeLogin')),
);

const UnsubscribeReminderPage = lazy(() =>
  componentLoader(() => import('features/Provider/Register/Unsubscribe')),
);

const ArticlePage = lazy(() =>
  componentLoader(() => import('features/Provider/Marketing/Public/Article')),
);

const AudioPage = lazy(() =>
  componentLoader(() => import('features/Provider/Audio')),
);

const PreviewShortPage = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/Provider/Experiences/WellniteAudios/Shorts/PreviewShortPage/PreviewShortPage'
      ),
  ),
);

const AudioLandingPage = lazy(() =>
  componentLoader(() => import('features/Provider/Audio/AudioLandingPage')),
);

const GenreDetailsPage = lazy(() =>
  componentLoader(() => import('features/Provider/Audio/GenreDetails')),
);

const directBookingLocalization = (basePath: string): JSX.Element | null => {
  const pathName = location.pathname;
  const hash = location.hash ?? '';

  if (!pathName.includes('/direct-booking')) return null;

  const localePattern = /\/[a-z]{2}-[a-z]{2}\b/gi;
  const matches = pathName.match(localePattern);

  const utmSource = useQuery().get('utm_source') ?? '';
  const query = utmSource ? `?utm_source=${utmSource}` : '';

  if (!matches || matches.length > 1) {
    const cleanedPathName = pathName.replace(localePattern, '');
    return (
      <Redirect
        to={`${basePath}${cleanedPathName}${query}${hash}`}
        key={`${basePath}${cleanedPathName}`}
      />
    );
  }

  if (!pathName.startsWith(basePath)) {
    return (
      <Redirect
        to={`${basePath}${pathName}${query}${hash}`}
        key={`${basePath}${pathName}`}
      />
    );
  }

  return null;
};

export const PublicRoutes = (): JSX.Element[] => {
  const {locale} = useLocale();
  const basePath = '/' + locale;
  const directBookingRedirect = directBookingLocalization(basePath);

  if (directBookingRedirect) {
    return [directBookingRedirect];
  }

  return [
    <PublicRoute
      exact
      path={ROUTES.HOME}
      key={ROUTES.HOME}
      component={WelcomePage}
      layoutProps={{
        bgColor: 'bg-blue-600',
        lgColumns: 'lg-grid-cols-12',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.LOGIN}
      key={ROUTES.LOGIN}
      component={LoginPage}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'lg:grid-cols-12',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.REFERRAL}
      key={ROUTES.REFERRAL}
      component={ReferralPage}
      layoutProps={{
        lgColumns: 'lg-grid-cols-1',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.FORGOT_PASSWORD}
      key={ROUTES.FORGOT_PASSWORD}
      component={RequestPasswordTokenPage}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'lg:grid-cols-12',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.RESET_PASSWORD_TOKEN}
      key={ROUTES.RESET_PASSWORD_TOKEN}
      component={ValidateResetTokenPage}
    />,
    <PublicRoute
      exact
      path={ROUTES.RESET_PASSWORD}
      key={ROUTES.RESET_PASSWORD}
      layout={AuthLayout}
      component={ChangePasswordPage}
    />,
    <PublicRoute
      exact
      path={ROUTES.GET_STARTED}
      key={ROUTES.GET_STARTED}
      component={GetStartedPage}
      layoutProps={{
        bgColor: 'bg-lightOrange',
        lgColumns: 'grid-cols-12',
      }}
    />,
    <PublicRoute
      exact
      path={basePath + ROUTES.DIRECT_BOOKING.GET_STARTED}
      key={basePath + ROUTES.DIRECT_BOOKING.GET_STARTED}
      component={DirectBookingPage}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'grid-cols-1',
      }}
    />,
    <PublicRoute
      exact
      path={`${basePath}${ROUTES.DIRECT_BOOKING.SEARCH_PROVIDERS}/:insuranceStatus(${InsuranceStatus.Insured}|${InsuranceStatus.Uninsured})?`}
      key={`${basePath}${ROUTES.DIRECT_BOOKING.SEARCH_PROVIDERS}/:insuranceStatus(${InsuranceStatus.Insured}|${InsuranceStatus.Uninsured})?`}
      component={DirectBookingSearchProvidersPage}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'grid-cols-1',
      }}
    />,
    <PublicRoute
      exact
      path={basePath + ROUTES.DIRECT_BOOKING.PROVIDERS}
      key={basePath + ROUTES.DIRECT_BOOKING.PROVIDERS}
      component={DirectBookingProviderListPage}
      layoutProps={{
        bgColor: 'bg-lightOrange',
        lgColumns: 'grid-cols-1',
      }}
    />,
    <PublicRoute
      exact
      path={basePath + ROUTES.DIRECT_BOOKING.PROVIDER}
      key={basePath + ROUTES.DIRECT_BOOKING.PROVIDER}
      component={DirectBookingProviderPage}
      layoutProps={{
        bgColor: 'bg-lightOrange',
        lgColumns: 'grid-cols-1',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.PLANS}
      key={ROUTES.PLANS}
      component={PlansPage}
      layoutProps={{
        bgColor: 'bg-lightOrange',
        lgColumns: 'grid-cols-1',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.REGISTER_FLOW}
      key={ROUTES.REGISTER_FLOW}
      component={RegisterPage}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'lg:grid-cols-12',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.PRACTICE_REGISTER}
      key={ROUTES.PRACTICE_REGISTER}
      component={RegisterPracticePage}
    />,
    <PublicRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION}
      key={ROUTES.PROVIDER.REGISTRATION}
      component={ProviderRegister}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
      }}
    />,
    <PublicRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION_THANK_YOU}
      key={ROUTES.PROVIDER.REGISTRATION_THANK_YOU}
      component={ProviderThankYou}
    />,
    <PublicRoute
      exact
      path={ROUTES.SELF_AWARENESS}
      key={ROUTES.SELF_AWARENESS}
      component={SelfAwarenessPage}
    />,

    <PublicRoute
      exact
      path={ROUTES.MINOR_CONSENT}
      key={ROUTES.MINOR_CONSENT}
      component={MinorConsent}
    />,

    <PublicRoute
      exact
      path={ROUTES.INTAKE_LOGIN}
      key={ROUTES.INTAKE_LOGIN}
      component={IntakeLoginPage}
    />,

    <PublicRoute
      exact
      path={ROUTES.UNSUBSCRIBE_ONBOARDING_REMINDER}
      key={ROUTES.UNSUBSCRIBE_ONBOARDING_REMINDER}
      component={UnsubscribeReminderPage}
    />,

    <PublicRoute
      exact
      path={ROUTES.POST}
      key={ROUTES.POST}
      component={ArticlePage}
      layout={ArticleLayout}
    />,

    <PublicRoute
      exact
      path={ROUTES.AUDIO}
      key={ROUTES.AUDIO}
      component={AudioPage}
      layout={PublicSingleAudioLayout}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'lg:grid-cols-1',
        navClasses: 'border-b border-gray-300',
      }}
    />,

    <PublicRoute
      exact
      path={ROUTES.SHORTS}
      key={ROUTES.SHORTS}
      component={PreviewShortPage}
      layout={AudioLayout}
    />,

    <PublicRoute
      exact
      path={ROUTES.AUDIOS.ROOT}
      key={ROUTES.AUDIOS.ROOT}
      component={AudioLandingPage}
      layout={PublicSingleAudioLayout}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'lg:grid-cols-1',
        navClasses: 'border-b border-gray-300',
      }}
    />,

    <PublicRoute
      exact
      path={ROUTES.AUDIOS.GENRES}
      key={ROUTES.AUDIOS.GENRES}
      component={GenreDetailsPage}
      layout={PublicSingleAudioLayout}
      layoutProps={{
        bgColor: 'bg-white',
        lgColumns: 'lg:grid-cols-1',
        navClasses: 'border-b border-gray-300',
      }}
    />,
  ];
};
